import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, formValueSelector, Field, getFormValues } from 'redux-form';
import Answers from './Answers';
import MuiAlert from '@material-ui/lab/Alert';
import FormCheckBoxWithLabel from '../legacyForm/FormCheckBoxWithLabel';
import VotingApprovals from './VotingApprovals';
import VoterIdentificationMethods from './VoterIdentificationMethods';
import { validateAnswers } from '../../models/budget/budgetSubmissionValidation';
import { Button, Table, TableBody } from '@material-ui/core';
import VoterDataReadOnly from './VoterDataPreview';
import ApplicationTableRow from './types/ApplicationTableRow';

const required = value => value ? undefined : 'Zgoda jest wymagana';
const validateForm = (form, props) => {
  const errors = validateAnswers(form, props);
  
  if (props.votingSettings.phoneVerificationEnabled
    && form.voter
    && form.voter.phoneNumber
    && /^\s*(\d\s*){9}$/.test(form.voter.phoneNumber)
    && (!form.phoneVerification || !form.phoneVerification.token)) {
    errors.voter = { phoneNumber: 'Numer telefonu musi zostać zweryfikowany' };
  }

  return errors;
};

const selector = formValueSelector('budgetVoteForm');

const getDistrictByStreet = (districts, streetName, number = null) => {
  for (const district of districts) {
    for (const street of district.streets) {
      if (street.street.toLowerCase() === streetName.toLowerCase()) {
        if (street.modifier === 'entire') {
          return district.district;
        } else if (street.modifier === 'list' && number !== null) {
          const houseNumbers = street.list.split(',').map(num => num.trim());
          if (houseNumbers.includes(number.toString())) {
            return district.district;
          }
        }
      }
    }
  }
  return undefined;
};

const numberFormatter = (value, numerals, wovalue) => {
  const t0 = value % 10, t1 = value % 100, vo = [];
  if (wovalue !== true)
      vo.push(value);
  if (value === 1 && numerals[1])
      vo.push(numerals[1]);
  else if ((value === 0 || (t0 >= 0 && t0 <= 1) || (t0 >= 5 && t0 <= 9) || (t1 > 10 && t1 < 20)) && numerals[0])
      vo.push(numerals[0]);
  else if (((t1 < 10 || t1 > 20) && t0 >= 2 && t0 <= 4) && numerals[2])
      vo.push(numerals[2]);
  return vo.join(' ');
};

let BudgetVoteForm = ({
  handleSubmit,
  submittingForm,
  votingSettings,
  unit,
  disabled,
  approvalConstraintOption,
  onApprovalConstraintPick,
  change,
  actions,
  voterData,
  voterIdentificationMethodId,
  error,
  valid,
  applicationIds,
  onSaveDraft,
  savingDraft,
  showSaveDraftButton,
  allValues }) => {
  
  const [step, setStep] = useState(1);
  
  const selectedProjects = (applicationIds || []).map(id => votingSettings.projects.find(project => project.id === id));
  const district = getDistrictByStreet(votingSettings.districtStreetsFilters, voterData ? voterData.address || '' : '', voterData ? voterData.number : '');

  const getOtherDistrictsVotesLeft = () => {
    const votesLeft = votingSettings.choiceLimits.max - (applicationIds ? applicationIds.length : 0);
    const otherDistrictsVotesLeft = votingSettings.choiceLimits.otherDistrictsMax - countOtherDistricts(applicationIds, votingSettings);
    return numberFormatter(Math.min(votesLeft, otherDistrictsVotesLeft), ['głosów', 'głos', 'głosy']);
  };

  const countOtherDistricts = (applicationIds, votingSettings) => {
    const district = getDistrictByStreet(votingSettings.districtStreetsFilters, voterData ? voterData.address || '' : '', voterData ? voterData.number : '');
    if (!district) return 0;
    
    return (applicationIds || []).filter(id => {
      const project = votingSettings.projects.find(p => p.id === id);
      return project && project.district !== district;
    }).length;
  };

  const calculateMaxOtherDistricts = () => {
    const votesLeft = votingSettings.choiceLimits.max - (applicationIds ? applicationIds.length: 0);
    const otherDistrictsVotesLeft = votingSettings.choiceLimits.otherDistrictsMax - countOtherDistricts(applicationIds, votingSettings);
    return Math.min(votesLeft, otherDistrictsVotesLeft);
  };

  const calculatedMyDistrictsAnswers = () => {
    return (applicationIds ? applicationIds.length: 0) - countOtherDistricts(applicationIds, votingSettings);
  };

  return (
    <form onSubmit={handleSubmit}>
      {step === 1 && (
        <React.Fragment>
          <div className="row">
            
            
            <div className="box-header-outside">
              <div className="col-sm-12">
                <div className="header">
                  <h2>Dane osoby głosującej</h2>
                </div>
              </div>
            </div>
            <div className="box">
              <div className="col-sm-12">
                <VoterIdentificationMethods
                  voterIdentificationMethods={votingSettings.voterIdentificationMethods}
                  change={change}
                  voterIdentificationMethodId={voterIdentificationMethodId}
                  actions={actions}
                  voterData={voterData}
                  customMessages={votingSettings.customMessages || {}}
                  budgetId={votingSettings.id}
                  requiresParentalConsent={votingSettings.requiresParentalConsent}
                  parentalConsentAgeLimit={votingSettings.parentalConsentAgeLimit}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12" style={{ marginRight: 15, marginLeft: 15, display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
              <Button onClick={() => setStep(step + 1)} color="primary" variant="contained" disabled={!valid || disabled}>Kolejny krok</Button>
            </div>
          </div>
        </React.Fragment>)}

      {step === 2 && (
        <React.Fragment>
          <div className="row">
            <div style={{ marginBottom: 50, width: '100%', marginLeft: 15, marginRight: 15 }}>
                <MuiAlert elevation={1} severity="info">
                  <div>Możesz oddać max. <strong>{numberFormatter(votingSettings.choiceLimits.max, ['głosów', 'głos', 'głosy'])}</strong> w ramach swojego obszaru zamieszkania.<br />
                  Jeśli chcesz oddać złoty głos na projekt poza swoim obszarem zaznacz <strong>{numberFormatter(votingSettings.choiceLimits.max - votingSettings.choiceLimits.otherDistrictsMax, ['projektów', 'projekt', 'projekty'])}</strong> i przejdź do kolejnego kroku</div>
                  </MuiAlert>
              </div>
            
            {district
              ? <Answers votingSettings={votingSettings} error={error} district={district} limitAnswers={votingSettings.choiceLimits.max} disabled={disabled} />
              : <div className="col-xs-12" style={{ textAlign: 'center' }}>
                  <p><strong>Nie znaleziono obszaru dla wybranego adresu</strong></p>
                </div>}
          </div>

          <div className="row">
            <div className="col-xs-12" style={{ marginRight: 15, marginLeft: 15, display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <Button onClick={() => setStep(step - 1)} color="primary" variant="contained" disabled={step <= 1}>Poprzedni krok</Button>
              <Button onClick={() => setStep(step + 1)} color="primary" variant="contained" disabled={!district}>Kolejny krok</Button>
            </div>
          </div>
        </React.Fragment>)}

      {step === 3 && (
        <React.Fragment>
          <div className="row">
            <div className="box">
                <div className="col-sm-12">
                  <h2 style={{ marginBottom: 0 }}>Złoty głos</h2>
                </div>
              </div>

              <div style={{ marginBottom: 50, width: '100%', marginLeft: 15, marginRight: 15 }}>
                <MuiAlert elevation={1} severity="info">
                  {applicationIds && votingSettings.choiceLimits.max === applicationIds.length
                  ? <div>Wszystkie głosy zostały wykorzystane. Jeśli chcesz zmienić swój wybór wróć do poprzedniego kroku</div>
                  : <div>W tym kroku możesz oddać tylko <strong>{numberFormatter(votingSettings.choiceLimits.otherDistrictsMax, ['głosów', 'głos', 'głosy'])}</strong> na projekt spoza Twojego obszaru.<br />
                    Pozostała liczba głosów do wykorzystania: <strong>{getOtherDistrictsVotesLeft()}</strong>
                    </div>}
                  </MuiAlert>
              </div>

              <Answers votingSettings={votingSettings} error={error} exludedDistrict={district} limitAnswers={calculatedMyDistrictsAnswers() === 0 ? votingSettings.choiceLimits.otherDistrictsMax : calculateMaxOtherDistricts() + calculatedMyDistrictsAnswers()} disabled={disabled} />
          </div>

          <div className="row">
            <div className="col-xs-12" style={{ marginRight: 15, marginLeft: 15, display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <Button onClick={() => setStep(step - 1)} color="primary" variant="contained" disabled={step <= 1}>Poprzedni krok</Button>
              <Button onClick={() => setStep(step + 1)} color="primary" variant="contained" disabled={!valid || !district}>Kolejny krok</Button>
            </div>
          </div>
        </React.Fragment>)}
      
      {step === 4 && (
        <React.Fragment>
          <div className="row">
            <div className="box-header-outside">
              <div className="col-sm-12">
                <div className="header">
                  <h2>Podsumowanie</h2>
                </div>
              </div>
            </div>
            
            <div className="box">
              <div className="col-sm-12">
                <h3>Dane osoby głosującej</h3>
                <VoterDataReadOnly voterData={voterData || {}} />
              </div>
            </div>

            <div className="box">
              <div className="col-sm-12">
                <h3>Wybrane projekty</h3>
                <Table className="projects-table">
                  <TableBody>
                    {selectedProjects.map(application => (
                      <ApplicationTableRow
                        key={application.id}
                        application={application}
                      />)
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="box">
              <div className="col-sm-12">
                <div className="rodo" style={{ marginTop: 20, marginBottom: 20 }}>
                  <div dangerouslySetInnerHTML={{ __html: unit.policy }} />
                </div>
                {votingSettings.predefinedApprovals.personalDataProcessing && <Field
                  name="acceptPrivacyPolicy"
                  component={FormCheckBoxWithLabel}
                  label="Wyrażam zgodę na przetwarzanie moich danych osobowych dla potrzeb związanych z realizacją Budżetu"
                  fullWidth
                  validate={required}
                  disabled={disabled}
                />}
                {votingSettings.predefinedApprovals.termsAndConditions && <Field
                  name="acceptTerms"
                  component={FormCheckBoxWithLabel}
                  label={<div>Oświadczam, że zapoznałem/am się z treścią <a href="/regulamin" target="_blank" rel="noopener noreferrer">Regulaminu</a> oraz akceptuję jego postanowienia</div>}
                  fullWidth
                  validate={required}
                  disabled={disabled}
                />}
                {votingSettings.approvals && votingSettings.approvals.length
                  ? <VotingApprovals approvals={votingSettings.approvals} approvalConstraintOption={approvalConstraintOption} onApprovalConstraintPick={onApprovalConstraintPick} disabled={disabled} />
                  : null}
              </div>
            </div>

          </div>

          <div className="row">
            <div className="col-xs-12" style={{ marginRight: 15, marginLeft: 15, display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <Button onClick={() => setStep(step - 1)} color="primary" variant="contained" disabled={step <= 1 || disabled}>Poprzedni krok</Button>
              <div>
                {showSaveDraftButton && (
                  <button
                    type="button"
                    onClick={e => { 
                      e.preventDefault();
                      // Always call onSaveDraft to handle validation display
                      onSaveDraft(allValues, valid); 
                    }}
                    disabled={savingDraft || disabled}
                    className="btn btn-secondary"
                    style={{ marginRight: 10 }}
                    aria-label="Zapisz wersję roboczą"
                  >
                    {savingDraft ? 'Zapisywanie...' : 'Zapisz wersję roboczą'}
                  </button>
                )}
                <button type="submit" disabled={submittingForm || disabled || !valid} className="btn btn-send" style={{ marginBottom: 0 }}>
                  {submittingForm ? 'Zapisywanie...' : 'Oddaj głos'} <i className="fa fa-paper-plane" />
                </button>
              </div>
            </div>
          </div>
          </React.Fragment>)}

    </form>
  );
};

BudgetVoteForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submittingForm: PropTypes.bool,
  details: PropTypes.array,
  votingSettings: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  unit: PropTypes.object.isRequired,
  approvalConstraintOption: PropTypes.string,
  onApprovalConstraintPick: PropTypes.func,
  actions: PropTypes.object.isRequired,
  change: PropTypes.func.isRequired,
  error: PropTypes.string,
  voterData: PropTypes.object,
  voterIdentificationMethodId: PropTypes.string,
  valid: PropTypes.bool,
  applicationIds: PropTypes.array,
  onSaveDraft: PropTypes.func,
  savingDraft: PropTypes.bool,
  showSaveDraftButton: PropTypes.bool,
  allValues: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    details: selector(state, 'details'),
    voterData: selector(state, 'voter'),
    applicationIds: selector(state, 'applicationIds'),
    voterIdentificationMethodId: selector(state, 'voterIdentificationMethodId'),
    allValues: getFormValues('budgetVoteForm')(state),
  };
}

BudgetVoteForm = reduxForm({
  form: 'budgetVoteForm',
  enableReinitialize: true,
  validate: validateForm,
})(BudgetVoteForm);

export default connect(mapStateToProps)(BudgetVoteForm);



// WEBPACK FOOTER //
// ./src/components/budget/BudgetVoteFormWizard.js